/**
 * @file This file contains the SettlementManager class, responsible for managing
 * all settlement buildings, their state, and their interactions.
 * @module SettlementManager
 */

import { Settlement } from '../models/Settlement.js';

/**
 * Manages all settlement instances on the grid, handling their lifecycle,
 * state updates (like inventory and cooldowns), and production logic.
 *
 * @export
 * @class SettlementManager
 * @typedef {SettlementManager}
 */
export class SettlementManager {
    /**
     * @param {object} dependencies - The dependencies for the SettlementManager.
     * @param {import('../models/Grid.js').Grid} dependencies.grid - The game grid instance.
     * @param {import('./UIManager.js').UIManager} dependencies.ui - The UI manager instance.
     * @param {import('./DebugManager.js').DebugManager} dependencies.debug - The debug manager instance.
     * @param {object} dependencies.masterItemsConfig - The master list of all item definitions.
     * @param {import('../Game.js').Game} dependencies.game - The main game instance.
     * @param {import('../lib/EventEmitter.js').EventEmitter} dependencies.eventEmitter - The event emitter instance.
     */
    constructor({ grid, ui, debug, masterItemsConfig, game, eventEmitter }) {
        /** @private */
        this.grid = grid;
        /** @private */
        this.ui = ui;
        /** @private */
        this.eventEmitter = eventEmitter;
        /** @private */
        this.debugManager = debug;
        /** @private */
        this.masterItemsConfig = masterItemsConfig;
        /** @private */
        this.game = game;
        /** @private */
        this.npcActionsConfig = game.npcActionsConfig;
  /**
         * A flattened map of all items for quick lookups.
         * @private
         * @type {Map<string, object>}
         */
        this.itemMap = new Map();
        /**
         * A set of all item keys that are used as imports in any recipe.
         * @private
         * @type {Set<string>}
         */
        this.allImportKeys = new Set();
        this._buildItemMap();
        /**
         * A map of all active settlement instances on the grid.
         * The key is a string "r,c" of the settlement's position.
         * @private
         * @type {Map<string, Settlement>}
         */
        this.settlements = new Map();
    }

    /**
     * Creates a flat map of all items for efficient O(1) lookups.
     * @private
     */
    _buildItemMap() {
        for (const category in this.masterItemsConfig) {
            for (const key in this.masterItemsConfig[category]) {
                this.itemMap.set(key, this.masterItemsConfig[category][key]);
            }
        }

        // Pre-calculate all possible import keys for efficient lookup.
        for (const settlementConfig of Object.values(this.masterItemsConfig.settlements)) {
            if (settlementConfig.recipes) {
                for (const recipe of settlementConfig.recipes) {
                    if (recipe.imports) Object.keys(recipe.imports).forEach(key => this.allImportKeys.add(key));
                }
            }
        }
    }

    /**
     * Initializes the manager, clearing any existing settlements.
     */
    initManager() {
        this.settlements.clear();
        this.debugManager.log('SettlementManager initialized.');
    }

    /**
     * Creates a new Settlement instance and adds it to the grid and the manager.
     * This should be called when placing a new settlement.
     * @param {number} r - The row to place the settlement.
     * @param {number} c - The column to place the settlement.
     * @param {object} settlementConfig - The configuration for the settlement from masterItemsConfig.
     */
    createSettlement(r, c, settlementConfig) {
        const position = { r, c };
        const settlement = new Settlement(settlementConfig, position);
        const posKey = `${r},${c}`;

        this.grid.set(r, c, settlement);
        this.settlements.set(posKey, settlement);

        this.debugManager.log(`Created settlement: ${settlement.name} at (${r},${c}).`);
        this._prepopulateInventory(settlement);
    }

    /**
     * Pre-populates a new settlement's inventory with starting items.
     * @param {Settlement} settlement - The settlement to populate.
     * @private
     */
    _prepopulateInventory(settlement) {
        // The warehouse is for storage and should always start empty.
        if (settlement.key === 'settlement_warehouse') {
            this.debugManager.log(`Warehouse ${settlement.name} starts empty.`);
            return;
        }

        const initialExportStock = 1;
        const initialImportStock = 2;

        settlement.recipes.forEach(recipe => {
            Object.keys(recipe.exports || {}).forEach(key => settlement.addToExports(key, initialExportStock));
            Object.keys(recipe.imports || {}).forEach(key => settlement.addToImports(key, initialImportStock));
        });
        this.debugManager.log(`Pre-populated ${settlement.name} with initial stock.`);
    }

    /**
     * Removes a settlement from a specific grid location.
     * This is used for external removal actions, like a debug tool.
     * @param {number} r - The row of the settlement.
     * @param {number} c - The column of the settlement.
     * @returns {boolean} True if a settlement was found and removed, false otherwise.
     */
    removeSettlement(r, c) {
        const posKey = `${r},${c}`;
        if (this.settlements.has(posKey)) {
            this.settlements.delete(posKey);
            this.debugManager.log(`Removed settlement at (${r},${c}).`);
            return true;
        }
        return false;
    }

    /**
     * Updates all active settlements. This should be called once per turn.
     */
    update() {
        // First, try to start production on any idle settlements that might have received ingredients.
        this.settlements.forEach(settlement => this._tryStartProduction(settlement));

        this.settlements.forEach(settlement => {
            // The update method now returns the full exports object {itemKey: quantity}.
            const finishedOutput = settlement.update();

            if (finishedOutput) {
                Object.entries(finishedOutput).forEach(([key, qty]) => {
                    settlement.addToExports(key, qty);
                    this.debugManager.log(`${settlement.name} finished producing and stocked ${qty}x ${key}.`);
                });

                const cellElement = this.ui.dom.grid.children[settlement.position.r * this.grid.size + settlement.position.c];
                if (cellElement) this.ui.updateGridCell(cellElement, settlement, this.game.gameState.activeGameConfig);
            }
        });
    }

    /**
     * Retrieves a settlement instance from a specific grid location.
     * @param {number} r - The row of the settlement.
     * @param {number} c - The column of the settlement.
     * @returns {Settlement|undefined} The settlement instance, or undefined if none exists.
     */
    getSettlementAt(r, c) {
        const posKey = `${r},${c}`;
        return this.settlements.get(posKey);
    }

    /**
     * Finds a settlement that has finished products (exports) in its inventory.
     * @returns {import('../models/Settlement.js').Settlement|null} The first settlement found with exports, or null.
     */
    findSettlementWithExports(excludePosition = null, justVisitedWarehouse = false) {
        const productionBuildingsWithExports = [];
        let warehouseWithExports = null;

        for (const settlement of this.settlements.values()) {
            if (settlement.exportsPaused) {
                continue;
            }

            if (excludePosition && settlement.position.r === excludePosition.r && settlement.position.c === excludePosition.c) {
                continue;
            }

            const hasExportItem = Object.values(settlement.exports).some(qty => qty > 0);
            if (!hasExportItem) {
                continue;
            }

            if (settlement.isUniversal) {
                // If the NPC just visited a warehouse, it should not go to another warehouse right away.
                if (!justVisitedWarehouse) {
                    warehouseWithExports = settlement;
                }
            } else {
                productionBuildingsWithExports.push(settlement);
            }
        }

        // Prioritize production buildings.
        if (productionBuildingsWithExports.length > 0) {
            // Return a random one to make behavior less predictable.
            return productionBuildingsWithExports[Math.floor(Math.random() * productionBuildingsWithExports.length)];
        }

        // If no production buildings have exports, fall back to a warehouse.
        if (warehouseWithExports) {
            return warehouseWithExports;
        }
        return null;
    }

    /**
     * Finds a settlement that has a recipe requiring a specific item.
     * @param {string} itemKey - The key of the item the NPC is carrying.
     * @param {{r: number, c: number}} [excludePosition] - Optional. A position to exclude from the search.
     * @returns {import('../models/Settlement.js').Settlement|null} The first settlement found that needs the item, or null.
     */
    findSettlementNeedingItem(itemKey, excludePosition = null) {
        const productionTargets = []; // All non-special settlements that can use the item in a recipe.
        let warehouseTarget = null; // Universal storage.
        let recyclerTarget = null; // Recycler for useless items.

        for (const settlement of this.settlements.values()) {
            // Categorize each settlement based on its type and needs.
            if (settlement.isUniversal) {
                warehouseTarget = settlement;
            } else if (settlement.acceptsNoActionItems) {
                recyclerTarget = settlement;
            } else {
                // This is a production building (e.g., Bank, Mint, Bakery).
                // Check if it has a recipe that imports the item.
                if (settlement.recipes.some(recipe => recipe.imports && recipe.imports[itemKey])) {
                    productionTargets.push(settlement);
                }
            }
        }

        // --- Priority Order for Drop-offs ---
        // 1. Prioritize any production building that needs the item.
        let filteredProductionTargets = productionTargets;
        // Only filter out the current location if the NPC is at a universal warehouse, to prevent warehouse-to-warehouse loops.
        if (excludePosition && this.getSettlementAt(excludePosition.r, excludePosition.c)?.isUniversal) {
            filteredProductionTargets = productionTargets.filter(s =>
                !(s.position.r === excludePosition.r && s.position.c === excludePosition.c)
            );
        }
        if (filteredProductionTargets.length > 0) {
            // Randomly pick one of the valid production targets.
            return filteredProductionTargets[Math.floor(Math.random() * filteredProductionTargets.length)];
        }

        // 2. If no production building needs it, check if it's recyclable.
        const itemConfig = this.itemMap.get(itemKey);
        const itemIsAnImport = this.allImportKeys.has(itemKey);
        const itemHasAction = itemConfig && itemConfig.type && this.npcActionsConfig[itemConfig.type];

        // An item is recyclable only if it's not an import and has no action.
        if (!itemIsAnImport && !itemHasAction && recyclerTarget) {
            return recyclerTarget;
        }

        // 3. If it's not needed and not recyclable, take it to a warehouse as a last resort.
        if (warehouseTarget && !(excludePosition && warehouseTarget.position.r === excludePosition.r && warehouseTarget.position.c === excludePosition.c)) {
            return warehouseTarget;
        }

        return null; // No suitable destination found.
    }

    /**
     * Finds the nearest settlement that has an item of a specific type in stock.
     * @param {string} itemType - The type of item to look for (e.g., 'food', 'tool').
     * @param {{r: number, c: number}} [excludePosition] - Optional. A position to exclude from the search.
     * @returns {import('../models/Settlement.js').Settlement|null} The first settlement found with the item type.
     */
    findSettlementWithItemType(itemType, excludePosition = null) {
        for (const settlement of this.settlements.values()) {
            if (excludePosition && settlement.position.r === excludePosition.r && settlement.position.c === excludePosition.c) {
                continue;
            }
            if(!settlement.exports){
                continue;
            }
            // Check if any item in the inventory matches the desired type.
            const hasItemType = Object.keys(settlement.exports).some(itemKey => {
                const itemConfig = this.itemMap.get(itemKey);
                return itemConfig && itemConfig.type === itemType && settlement.exports[itemKey] > 0;
            });

            if (hasItemType) return settlement;
        }
        return null;
    }

    /**
     * Handles the interaction between an NPC and a settlement.
     * This now distinguishes between dropping off an item and picking one up.
     * @param {Character} npc - The NPC interacting.
     * @param {Settlement} settlement - The settlement being interacted with.
     */
    handleNpcInteraction(npc, settlement) {
        if (npc.inventory.accessory) {
            // NPC is carrying something: Try to DROP OFF.
            this._handleItemDropOff(npc, settlement);
        } else {
            // NPC is empty-handed: Try to PICK UP.
            this._handleItemPickUp(npc, settlement);
        }
    }

    /**
     * Handles an NPC attempting to drop off an item at a settlement.
     * @private
     * @param {Character} npc - The NPC carrying an item.
     * @param {Settlement} settlement - The target settlement.
     */
    _handleItemDropOff(npc, settlement) {
        const npcItemKey = npc.inventory.accessory.key;
        const npcItemConfig = npc.inventory.accessory;

        // If the settlement is universal, it can accept any item.
        if (settlement.isUniversal) {
            this._acceptItem(npc, settlement, npcItemKey);
            return;
        }

        // If the NPC is a consumer, it will "eat" the food item directly.
        if (npc.state.role === 'consumer' && npcItemConfig.type === 'food') {
            this.debugManager.log(`NPC ${npc.id} ate a ${npc.inventory.accessory.name} at ${settlement.name}.`);
            this._consumeItem(npc);
            return;
        }

        // NEW: Special logic for the recycler
        if (settlement.acceptsNoActionItems) {
            const itemIsAnImport = this.allImportKeys.has(npcItemKey);
            const itemHasAction = npcItemConfig.type && this.npcActionsConfig[npcItemConfig.type];

            if (!itemIsAnImport && !itemHasAction) {
                this._acceptRecycledItem(npc, settlement, npcItemKey);
                return;
            }
        }

        // Find a recipe that accepts the item the NPC is carrying.
        const validRecipe = settlement.recipes.find(recipe => recipe.imports && recipe.imports[npcItemKey]);

        if (!validRecipe) {
            this.debugManager.log(`NPC ${npc.id} has a ${npc.inventory.accessory.name}, but ${settlement.name} has no valid recipe for it.`);
            return;
        }

        this._acceptItem(npc, settlement, npcItemKey);
    }

    /**
     * Helper method to handle the logic of an NPC successfully dropping off an item.
     * @private
     * @param {Character} npc - The NPC.
     * @param {Settlement} settlement - The settlement.
     * @param {string} itemKey - The key of the item being dropped off.
     */
    _acceptItem(npc, settlement, itemKey) {
        // --- Successful Drop-off ---
        this.debugManager.log(`NPC ${npc.id} dropped off ${itemKey} at ${settlement.name}.`);
        
        // 1. Take item from NPC and add to settlement inventory.
        settlement.isUniversal ? settlement.addToExports(itemKey, 1) : settlement.addToImports(itemKey, 1);
        npc.inventory.accessory = null;
        npc.inventory.lastDroppedItem = itemKey; // Remember what was just dropped.
        npc.state.isLeaving = false; // Ensure the NPC doesn't continue leaving after a drop-off.
        npc.state.role = 'distributor'; // The NPC is now a distributor, looking for an export.

        // Force path re-evaluation on the next tick.
        npc.movement.path = [];
        npc.movement.cooldown = 0;


        // --- TRIGGER UI UPDATE ---
        const cellElement = this.ui.dom.grid.children[settlement.position.r * this.grid.size + settlement.position.c];
        if (cellElement) this.ui.updateGridCell(cellElement, settlement, this.game.gameState.activeGameConfig);


        // 2. If it's a production settlement, try to start production.
        if (!settlement.isUniversal) {
            this._tryStartProduction(settlement);
        }

        // 3. After any successful drop-off, check if the now empty-handed NPC can pick up a finished product from this settlement.
        if (Object.values(settlement.exports).some(qty => qty > 0)) {
            this._handleItemPickUp(npc, settlement);
        }
    }

    /**
     * Handles an NPC dropping off an item at the recycling center.
     * @private
     */
    _acceptRecycledItem(npc, settlement, itemKey) {
        this.debugManager.log(`NPC ${npc.id} recycled ${itemKey} at ${settlement.name}.`);
        npc.inventory.accessory = null;
        npc.state.role = 'distributor';
        npc.movement.path = [];
        npc.movement.cooldown = 0;

        if (settlement.recipes[0]?.exports) {
            settlement.startProduction(settlement.recipes[0].exports);
        }

        // 3. After any successful drop-off, check if the now empty-handed NPC can pick up a finished product from this settlement.
        if (Object.values(settlement.exports).some(qty => qty > 0)) {
            this._handleItemPickUp(npc, settlement);
        }
    }

    /**
     * Checks if a settlement has the ingredients for a recipe and starts production if it's idle.
     * @private
     * @param {Settlement} settlement - The settlement to check.
     */
    _tryStartProduction(settlement) {
        // Can't start a new production run if it's already busy, or if it's the recycler (which has special logic).
        if (settlement.acceptsNoActionItems) {
            return;
        }

        if (settlement.isOnCooldown()) {
            return;
        }

        // Find all recipes for which the settlement has all ingredients.
        const possibleRecipes = settlement.recipes.filter(recipe => settlement.hasIngredients(recipe.imports));

        if (possibleRecipes.length > 0) {
            // If multiple recipes are possible, pick one at random.
            const recipeToStart = possibleRecipes[Math.floor(Math.random() * possibleRecipes.length)];

            this.debugManager.log(`${settlement.name} has all ingredients for a recipe. Starting production.`);

            // Consume ingredients from inventory.
            if (recipeToStart.imports) {
                Object.entries(recipeToStart.imports).forEach(([key, quantity]) => {
                    settlement.removeFromImports(key, quantity);
                });
            }

            // Start production cooldown.
            settlement.startProduction(recipeToStart.exports);

            // Show a "working" effect.
            this.ui.addTemporaryEffect(settlement.position.r, settlement.position.c, 'working', { x: settlement.position.c, gridWidth: this.grid.size });

            // Trigger a UI update for the specific cell to reflect the inventory change.
            const cellElement = this.ui.dom.grid.children[settlement.position.r * this.grid.size + settlement.position.c];
            if (cellElement) this.ui.updateGridCell(cellElement, settlement, this.game.gameState.activeGameConfig);
        }
    }

    /**
     * Handles the logic for an NPC consuming an item (like eating food).
     * @private
     * @param {Character} npc The NPC consuming the item.
     */
    _consumeItem(npc) {
        npc.inventory.accessory = null;
        npc.attributes.hunger = 0; // Reset hunger
        npc.state.role = 'distributor'; // After eating, look for a new job
        npc.movement.path = [];
        npc.movement.cooldown = 0;
        this.eventEmitter.emit('achievement:itemConsumed', { npc });
    }

    /**
     * Handles an empty-handed NPC attempting to pick up an item from a settlement.
     * @private
     * @param {Character} npc - The empty-handed NPC.
     * @param {Settlement} settlement - The target settlement.
     */
    _handleItemPickUp(npc, settlement) {
        // Filter out the item the NPC just dropped, if any.
        let availableItems = Object.keys(settlement.exports).filter(key => {
            return settlement.exports[key] > 0 && key !== npc.inventory.lastDroppedItem;
        });

        // Special rule for crowns: only elders can pick them up.
        availableItems = availableItems.filter(key => {
            if (key === 'accessory_crown') {
                return npc.config.isElder;
            }
            return true;
        });

        if (availableItems.length === 0) {
            const allItems = Object.keys(settlement.exports).filter(key => settlement.exports[key] > 0);
            if (allItems.length > 0) {
                 this.debugManager.log(`NPC ${npc.id} visited ${settlement.name}, but the only available items are ones it just dropped or is not eligible to pick up.`);
            } else {
                 this.debugManager.log(`NPC ${npc.id} visited ${settlement.name}, but it has no suitable items in stock.`);
            }
            npc.inventory.lastDroppedItem = null; 
            return;
        }

        let itemToPickUpKey;
        // An NPC can pick up any available export. The decision of where to take it
        // is handled by the pathfinding logic (`findSettlementNeedingItem`).
        itemToPickUpKey = availableItems[Math.floor(Math.random() * availableItems.length)];

        if (!itemToPickUpKey) {
            this.debugManager.log(`NPC ${npc.id} visited ${settlement.name}, but found no suitable items to pick up.`);
            npc.inventory.lastDroppedItem = null;
            return;
        }

        const pickedUp = settlement.removeFromExports(itemToPickUpKey, 1);

        if (!pickedUp) {
            this.debugManager.log(`Error: Failed to remove ${itemToPickUpKey} from ${settlement.name} inventory.`);
            return;
        }

        const exportedItemConfig = this.itemMap.get(itemToPickUpKey);

        if (exportedItemConfig) {
            npc.inventory.accessory = exportedItemConfig;
            npc.state.role = 'hauler'; // NPC now has an item, so it's a hauler.
            npc.inventory.lastDroppedItem = null;
            if (settlement.isUniversal) npc.memory.justVisitedWarehouse = true;
            this.debugManager.log(`NPC ${npc.id} picked up a ${exportedItemConfig.name} from ${settlement.name}.`);
            const cellElement = this.ui.dom.grid.children[settlement.position.r * this.grid.size + settlement.position.c];
            if (cellElement) this.ui.updateGridCell(cellElement, settlement, this.game.gameState.activeGameConfig);
        } else {
            settlement.addToExports(itemToPickUpKey, 1);
            this.debugManager.log(`Error: Could not find item config for ${itemToPickUpKey}.`);
        }
    }

    /**
     * Finds the full configuration object for an item by its key. (DEPRECATED - use this.itemMap.get())
     */

    /**
     * Gets the current state of all settlements for saving.
     * @returns {Array<object>} An array of settlement state objects.
     */
    getState() {
        const settlementStates = [];
        for (const settlement of this.settlements.values()) {
            settlementStates.push(settlement.getState());
        }
        return settlementStates;
    }

    /**
     * Restores the state of all settlements from a saved state.
     * @param {Array<object>} settlementStates - The array of saved settlement states.
     */
    restoreState(settlementStates) {
        if (!settlementStates) return;

        this.initManager(); // Clear existing settlements

        for (const state of settlementStates) {
            const settlementConfig = this.itemMap.get(state.key);
            if (settlementConfig) {
                // Create a new stateful instance
                this.createSettlement(state.pos.r, state.pos.c, settlementConfig);
                const settlement = this.getSettlementAt(state.pos.r, state.pos.c);
                settlement.imports = state.imports || {};
                settlement.exports = state.exports || {};
                settlement.cooldown = state.cooldown || 0;
                settlement.productionOutput = state.productionOutput || null;
                settlement.importsPaused = state.importsPaused || false;
                settlement.exportsPaused = state.exportsPaused || false;
            }
        }
    }

    /**
     * Updates multiple properties of a settlement in a single batch.
     * @param {string} settlementKey - The key of the settlement to update (e.g., "r,c").
     * @param {Array<{property: string, value: any}>} updates - An array of property-value pairs.
     */
    batchUpdateSettlementProperties(settlementKey, updates) {
        const settlement = this.settlements.get(settlementKey);
        if (!settlement) {
            this.debugManager.log(`Attempted to batch update on non-existent settlement key: ${settlementKey}`);
            return;
        }

        updates.forEach(({ property, value }) => {
            if (property.startsWith('imports.')) {
                const itemKey = property.split('.')[1];
                if (value > 0) {
                    settlement.imports[itemKey] = value;
                } else {
                    delete settlement.imports[itemKey];
                }
                this.debugManager.log(`Updated imports for ${itemKey} in ${settlement.name} to ${value}.`);
            } else if (property.startsWith('exports.')) {
                const itemKey = property.split('.')[1];
                if (value > 0) {
                    settlement.exports[itemKey] = value;
                } else {
                    delete settlement.exports[itemKey];
                }
                this.debugManager.log(`Updated exports for ${itemKey} in ${settlement.name} to ${value}.`);
            } else if (property === 'importsPaused') {
                settlement.importsPaused = value;
                this.debugManager.log(`Updated importsPaused for ${settlement.name} to ${value}.`);
            } else if (property === 'exportsPaused') {
                settlement.exportsPaused = value;
                this.debugManager.log(`Updated exportsPaused for ${settlement.name} to ${value}.`);
            }
        });
    }
}